import React from "react";

import { INavLink } from "./types";
import useWindowSize from "./useWindowSize";

type Props = {
  links: INavLink[];
  offset?: number; //px
  isRTL?: boolean;
};

function arraysEqual<T>(a: Array<T>, b: Array<T>): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

const usePriorityNav = ({ links, offset = 0, isRTL = false }: Props) => {
  const windowSize = useWindowSize();
  const [childrenWidths, setWidths] = React.useState<number[]>([]);
  const [visibleLinks, setVisibleLinks] = React.useState<INavLink[]>(links);
  const [nonVisibleLinks, setNonVisibleLinks] = React.useState<INavLink[]>([]);

  const prevLinksRef = React.useRef(links);
  const widthsRef = React.useRef<number[]>([]);

  React.useEffect(() => {
    if (prevLinksRef.current !== links) {
      setWidths([]);
      setVisibleLinks(links);
      setNonVisibleLinks([]);
      prevLinksRef.current = links;
      widthsRef.current = [];
    }
  }, [links]);

  // StrictMode can call refs twice, so set width by index to avoid duplicates.
  const measuredRef = React.useCallback(
    (node: HTMLLIElement | null, index: number) => {
      if (!node) return;

      const next = isRTL
        ? node.getBoundingClientRect().left + offset
        : node.getBoundingClientRect().right + offset;

      setWidths((prev) => {
        if (prev[index] === next) return prev;
        const copy = prev.slice();
        copy[index] = next;
        return copy;
      });
    },
    [offset, isRTL],
  );

  /**
   * Compute visible and non visible ref elements
   */
  React.useLayoutEffect(() => {
    // called first before childrenWidths are computed
    if (childrenWidths.length === 0) {
      return;
    }

    // After orientation change window.screen.width
    // can be smaller than windowSize.innerWidth,
    // so select smallest of two.
    const width =
      window.screen.width < windowSize.innerWidth
        ? window.screen.width
        : windowSize.innerWidth;

    const filteredIndexedElements = childrenWidths.filter(
      (e) => e < width - offset,
    );
    const newVisibleLinks = links.filter(
      (_, i) => i < filteredIndexedElements.length,
    );
    const newNonVisibleLinks = links.filter(
      (_, i) => i >= filteredIndexedElements.length,
    );

    // only update links if they are different to previous visible links
    const areEqual =
      arraysEqual<INavLink>(visibleLinks, newVisibleLinks) &&
      arraysEqual<INavLink>(nonVisibleLinks, newNonVisibleLinks);
    if (!areEqual) {
      setVisibleLinks(newVisibleLinks);
      setNonVisibleLinks(newNonVisibleLinks);
    }
  }, [
    childrenWidths,
    windowSize.innerWidth,
    links,
    visibleLinks,
    nonVisibleLinks,
    offset,
  ]);

  return [measuredRef, visibleLinks, nonVisibleLinks] as [
    typeof measuredRef,
    INavLink[],
    INavLink[],
  ];
};

export default usePriorityNav;
